import * as React from 'react'
import * as va from '@vercel/analytics'
import ph from 'posthog-js'
import { type Block, type TextBlock } from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'

import * as PropertiesPanel from '../../properties-panel'
import { Button } from '~/sotano-kit/primitives/button'
import { BlockIcon } from '~/components/block-icon'
import { useEditorStore } from '../../editor-store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getId } from '@basehub/utils/id/index'
import { toast } from 'sonner'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { useEditorMode } from '../../editor-mode-provider'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { ChangeBlockTypeParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/change'
import { useTabsStore } from '../../tabs/store'

export const TEXT_BLOCK_REGEX_PRESETS: Array<{ title: string; value: string }> =
  [
    {
      title: 'Email',
      value: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
    },
    {
      title: 'URL',
      value: '^https?://[^\\s/$.?#].[^\\s]*$',
    },
    {
      title: 'Slug',
      value: '^[a-z0-9-]+$',
    },
    {
      title: 'Phone',
      value: '^\\+?[0-9]{1,3}-?[0-9]{3,4}-?[0-9]{3,4}$',
    },
    {
      title: 'Emoji',
      value: '^[\\uD800-\\\uDBFF\\uDC00-\\\uDFFF]+$',
    },
  ]

export const TextBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: TextBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const editorMode = useEditorMode()
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const handleConversion = React.useCallback(async () => {
    if (!replicache) throw new Error('No replicache')
    if (editorMode !== 'write:all') {
      return
    }

    const params: ChangeBlockTypeParams = {
      blockId: block.id,
      title: block.title,
      path: path,
      isoNow: new Date().toISOString(),
      typeOptions: {},
      idSeed: getId(),
      newBlockType: 'rich-text',
    }
    const result = await replicache.mutate
      .changeBlockType(params)
      .catch((err) => {
        if (err instanceof MutatorUserError) {
          toast.error(`Failed to convert block: ${err.reason}`)
        } else {
          console.error(err)
          toast.error('Failed to convert block')
        }
      })

    if (result?.undo) {
      undoManager.current?.push({
        redo: [
          getInputFocusOp(`el-${sourceBlockId}-pp-convert-to-rich-text`),
          { op: 'changeBlockType', args: params },
        ],
        undo: [
          getInputFocusOp(`el-${sourceBlockId}-pp-convert-to-rich-text`),
          {
            op: 'blockBatchMutations',
            args: { mutations: result.undo },
          },
        ],
      })
    }

    setSelectedBlockId(sourceBlockId)
    va.track('Plain text converted to rich text')
    ph.capture('Plain text converted to rich text')
  }, [
    block.id,
    block.title,
    editorMode,
    getInputFocusOp,
    path,
    replicache,
    setSelectedBlockId,
    sourceBlockId,
    undoManager,
  ])

  return (
    <>
      <PropertiesPanel.PropertiesContainer>
        <PropertiesPanel.Group title="Constraints" spacing="tight">
          <PropertiesPanel.BooleanOption
            name="Is required"
            value={block.constraints.isRequired}
            id={`el-${sourceBlockId}-pp-is-required`}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  constraints: { isRequired: v },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
          />
          <PropertiesPanel.TextOption
            horizontal
            className="!mt-2.5"
            name="Min length"
            id={`${idToSourceBlockId(block)}-pp-min-length`}
            value={block.constraints.minLength?.toString() ?? ''}
            type="number"
            placeholder="Unset"
            inputProps={{ className: 'max-w-[230px]' }}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  constraints: { minLength: v === '' ? null : parseInt(v) },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-min-length`),
                    { op: 'updateBlock', args: params },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-min-length`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-min-length`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-min-length`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                })
              }
            }}
          />
          <PropertiesPanel.TextOption
            horizontal
            name="Max length"
            id={`${idToSourceBlockId(block)}-pp-max-length`}
            type="number"
            placeholder="Unset"
            value={block.constraints.maxLength?.toString() ?? ''}
            inputProps={{ className: 'max-w-[230px]' }}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: {
                  constraints: { maxLength: v === '' ? null : parseInt(v) },
                },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-max-length`),
                    { op: 'updateBlock', args: params },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-max-length`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-max-length`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-max-length`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                })
              }
            }}
          />
          <PropertiesPanel.TextOptionWithPresets
            presets={TEXT_BLOCK_REGEX_PRESETS}
            horizontal
            name="Regex pattern"
            id={`${idToSourceBlockId(block)}-pp-regex-pattern`}
            value={block.constraints.regexPattern ?? ''}
            placeholder="Unset"
            inputProps={{ className: 'max-w-[230px]' }}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                instanceChildTarget: block.instanceChildTarget,
                data: { constraints: { regexPattern: v === '' ? null : v } },
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-regex-pattern`),
                    { op: 'updateBlock', args: params },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-regex-pattern`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-regex-pattern`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-regex-pattern`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                })
              }
            }}
          />
        </PropertiesPanel.Group>

        <PropertiesPanel.CommonSearchConfigGroup
          block={block}
          parent={parent}
          path={path}
        />
        {editorMode === 'write:all' && (
          <PropertiesPanel.ConfigureContainer block={block}>
            <PropertiesPanel.Group noBorderBottom>
              <Button
                intent="secondary"
                onClick={handleConversion}
                id={`el-${sourceBlockId}-pp-convert-to-rich-text`}
                icon={
                  <BlockIcon
                    block={{ type: 'rich-text' }}
                    iconProps={{ className: 'mr-0.5' }}
                  />
                }
              >
                Convert to Rich Text
              </Button>
            </PropertiesPanel.Group>
          </PropertiesPanel.ConfigureContainer>
        )}
      </PropertiesPanel.PropertiesContainer>
    </>
  )
}
